/**
 * CompletedView — the DECIDED-intake archive (Workspace v2 Slice 2; Brief 3
 * Design B; conversion-view fix). An intake lands here once it reaches a
 * decision: CONVERTED to a client (the terminal "Move to Completed" advance — a
 * won client, client_id set) or CLOSED without converting. Each row states its
 * OUTCOME plainly so the list is usable for referral/conversion analytics:
 *   - Converted → an affirm (green) pill (client_id set — a won client).
 *   - Closed → a neutral pill + the close reason.
 *   - Outcome not recorded → a CAUTION (gold) chip (awaiting a human) PLUS an
 *     inline "Record outcome" affordance that backfills the missing reason
 *     without reopening the whole intake (reuses the close writer; the original
 *     decided date is preserved server-side).
 * Rows also show the resolved REFERRAL SOURCE (Directory name via the merge
 * chain) — the field that makes referral analytics possible. A server-side name
 * search (ADR-015), an All / Converted / Closed outcome filter (server-side, so
 * the count + pagination stay honest), Prev/Next off the server `total`, and
 * Reopen (clears closed_at → back to the pipeline). Rows open the drawer (read).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { useWorkItems, useReopenIntake, useCloseIntake, useIntakeOptions } from '@/lib/workspace';
import type { WorkItemRow } from '@/lib/api';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SkeletonRow } from '@/components/Skeleton';
import { Chip } from '@/components/primitives/Chip';
import { FilterPill } from '@/components/primitives/FilterPill';
import { PaginationFooter } from '@/components/primitives/PaginationFooter';
import { cardName, shortDate } from './workspaceFormat';
import { SelectField } from '@/components/primitives/SelectField';

const PAGE = 25;

type OutcomeFilter = 'all' | 'converted' | 'closed_unconverted';

export interface CompletedViewProps {
  /** The shared header search term (raw); debounced here into the server query. */
  query: string;
  canManage: boolean;
  onOpen: (id: string) => void;
}

/** The decided outcome of a closed row: converted == a won client, i.e. client_id
 *  set (native Move-to-Completed always sets it) OR close_reason='completed'
 *  (imported history whose client link lands at the cutover — the same widened
 *  signal the referral/conversion reports use, #542). Otherwise a recorded reason
 *  is a close; blank is not-recorded. */
function rowOutcome(r: WorkItemRow): 'converted' | 'closed' | 'not_recorded' {
  if (r.client_id || r.close_reason === 'completed') return 'converted';
  if (r.close_reason && r.close_reason.trim() !== '') return 'closed';
  return 'not_recorded';
}

export function CompletedView({ query, canManage, onOpen }: CompletedViewProps): ReactNode {
  const { tenantSlug } = useTenant();
  const reopen = useReopenIntake(tenantSlug);

  const [offset, setOffset] = useState(0);
  const [outcome, setOutcome] = useState<OutcomeFilter>('all');

  // Debounce the header search into the server request (no request per keystroke).
  const [debouncedQ, setDebouncedQ] = useState(query.trim());
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // A new search OR a new outcome filter resets to the first page.
  useEffect(() => { setOffset(0); }, [debouncedQ, outcome]);

  const { rows, total, isLoading, isError, error, refetch } = useWorkItems(
    tenantSlug,
    {
      kind: 'intake',
      bucket: 'closed',
      outcome: outcome === 'all' ? undefined : outcome,
      q: debouncedQ || undefined,
      limit: PAGE,
      offset,
    },
    { enabled: true },
  );

  const FILTERS: { key: OutcomeFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'converted', label: 'Converted' },
    { key: 'closed_unconverted', label: 'Closed' },
  ];

  return (
    <div data-testid="completed-view">
      <div className="flex flex-wrap gap-1.5 mb-3" role="group" aria-label="Outcome filter">
        {FILTERS.map((f) => (
          <FilterPill
            key={f.key}
            label={f.label}
            active={outcome === f.key}
            data-testid={`completed-filter-${f.key}`}
            onClick={() => setOutcome(f.key)}
          />
        ))}
      </div>

      {isLoading ? (
        <div><SkeletonRow /><SkeletonRow /></div>
      ) : isError ? (
        <ErrorBanner error={error} onRetry={refetch} what="the completed cards" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-soft px-2 py-4" data-testid="completed-empty">
          {debouncedQ ? `No decided intakes match “${debouncedQ}”.` : "Once an intake converts or closes, you'll see it here."}
        </p>
      ) : (
        <>
          <div className="max-w-3xl" data-testid="completed-rows">
            {rows.map((r) => (
              <CompletedRow key={r.id} row={r} canManage={canManage} onOpen={onOpen} reopenPending={reopen.isPending} onReopen={() => reopen.mutate(r.id)} />
            ))}
          </div>

          <PaginationFooter
            offset={offset}
            pageSize={PAGE}
            total={total}
            onOffsetChange={setOffset}
            testIdPrefix="completed-"
            className="flex items-center gap-3 mt-3 max-w-3xl text-[12.5px] text-sage-400"
          />
        </>
      )}
    </div>
  );
}

function CompletedRow({
  row: r,
  canManage,
  onOpen,
  reopenPending,
  onReopen,
}: {
  row: WorkItemRow;
  canManage: boolean;
  onOpen: (id: string) => void;
  reopenPending: boolean;
  onReopen: () => void;
}): ReactNode {
  const outcome = rowOutcome(r);
  const [recording, setRecording] = useState(false);

  return (
    <div className="flex items-center gap-3 bg-paper rounded-card px-4 py-3 mb-2 shadow-sh1" data-testid="completed-row" data-outcome={outcome}>
      <button type="button" onClick={() => onOpen(r.id)} className="text-left flex-1 min-w-0 focus-visible:outline-none">
        <div className="font-medium text-ink truncate">{r.display_name ?? cardName(r)}</div>
        <div className="text-xs text-sage-400 mt-0.5 truncate">
          {r.contact_name ? `Caller: ${r.contact_name}` : 'No caller recorded'}
          {r.referral_source_label ? ` · via ${r.referral_source_label}` : ''}
        </div>
      </button>
      <div className="flex items-center gap-2.5 flex-shrink-0">
        {/* Outcome marker — the register carries the meaning (affirm = won,
            neutral = closed with a reason, caution = awaiting a human). */}
        {outcome === 'converted' ? (
          <Chip variant="affirm" data-testid="completed-outcome-converted">Converted</Chip>
        ) : outcome === 'closed' ? (
          <Chip variant="neutral" data-testid="completed-outcome-closed">Closed · {r.close_reason}</Chip>
        ) : (
          <Chip variant="caution" data-testid="completed-outcome-not-recorded">Outcome not recorded</Chip>
        )}
        {canManage && outcome === 'not_recorded' && !recording && (
          <button type="button" data-testid="completed-record-outcome" onClick={() => setRecording(true)}
            className="text-xs font-semibold text-sage-800 underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
            Record outcome
          </button>
        )}
        <span className="text-[11.5px] text-sage-400 tabular-nums">{shortDate(r.closed_at)}</span>
        {canManage && (
          <button type="button" data-testid="completed-reopen" disabled={reopenPending}
            onClick={onReopen}
            className="text-xs font-semibold text-sage-800 underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 disabled:opacity-50">
            Reopen
          </button>
        )}
      </div>
      {recording && <RecordOutcomeControl row={r} onDone={() => setRecording(false)} />}
    </div>
  );
}

/**
 * Inline outcome-cleanup for a not-recorded closed row. Recording a close reason
 * reuses the close writer (no new mutation shape); the server preserves the
 * original decided date. A genuine WIN isn't a field-flip (it mints a client
 * record), so that path routes through Reopen — stated here rather than faked.
 */
function RecordOutcomeControl({ row, onDone }: { row: WorkItemRow; onDone: () => void }): ReactNode {
  const { tenantSlug } = useTenant();
  const close = useCloseIntake(tenantSlug);
  const { lists } = useIntakeOptions(tenantSlug);
  const [reason, setReason] = useState('');

  return (
    <div className="w-full mt-2.5 flex flex-wrap items-center gap-2 border-t border-sage-100 pt-2.5" data-testid="completed-record-panel">
      <span className="text-xs text-sage-400">Closed because…</span>
      <SelectField size="compact" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="completed-record-reason" aria-label="Close reason"
        >
        <option value="">Select a reason</option>
        {lists.close_reason.map((cr) => <option key={cr} value={cr}>{cr}</option>)}
      </SelectField>
      <button type="button" data-testid="completed-record-save" disabled={!reason || close.isPending}
        onClick={() => close.mutate({ id: row.id, reason }, { onSuccess: onDone })}
        className="text-xs font-semibold text-cream bg-sage-800 rounded-base px-2.5 py-1 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
        Save
      </button>
      <button type="button" onClick={onDone} className="text-xs font-semibold text-sage-400 hover:text-sage-800 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
        Cancel
      </button>
      <span className="text-[11px] text-sage-400 basis-full">Became a client? Reopen the intake to set up the client record.</span>
    </div>
  );
}
